/**
 * Session draft for Ari expert signup chat (TP-496).
 * Answers live client-side until email → passwordless auth; then
 * `/signup-ari/complete` persists profile + Ari criteria.
 */

export const SIGNUP_ARI_DRAFT_KEY = 'twine_signup_ari_draft';
/** Session flag so `/ari` auto-runs the first search after signup (TP-497). */
export const SIGNUP_ARI_PENDING_SEARCH_KEY = 'twine_signup_ari_pending_search';
export const SIGNUP_ARI_COMPLETE_PATH = '/signup-ari/complete';
export const SIGNUP_ARI_PATH = '/signup-ari';

export const SIGNUP_ARI_STEPS = [
  'name',
  'country',
  'city',
  'roles',
  'experience',
  'newsletter',
  'email',
];

const EMPTY_ANSWERS = {
  name: '',
  country: '',
  countryCode: '',
  city: '',
  roles: [],
  experience: [],
  newsletter: null,
  email: '',
};

function canUseSessionStorage() {
  if (typeof window === 'undefined') return false;
  try {
    // Accessing sessionStorage can throw (e.g. Safari private mode).
    return Boolean(window.sessionStorage);
  } catch (e) {
    return false;
  }
}

/** Settings / onboarding notification keys for job_alert_experience_level. */
const JOB_ALERT_EXPERIENCE_LABEL_TO_KEY = {
  Amateur: 'amateur',
  Beginner: 'beginner',
  Junior: 'junior',
  'Mid-level': 'mid_level',
  Senior: 'senior',
  Expert: 'expert',
};

/**
 * Map Ari signup experience labels → preferences.email.job_alert_experience_level.
 * Selected levels are "1", others "0" (same shape as settings defaults).
 */
export function buildJobAlertExperienceLevel(selectedLabels = []) {
  const selected = new Set(
    (Array.isArray(selectedLabels) ? selectedLabels : [])
      .map((label) => String(label).trim())
      .filter(Boolean)
  );
  const result = {};
  Object.keys(JOB_ALERT_EXPERIENCE_LABEL_TO_KEY).forEach((label) => {
    const key = JOB_ALERT_EXPERIENCE_LABEL_TO_KEY[label];
    result[key] = selected.has(label) ? '1' : '0';
  });
  return result;
}

/**
 * @returns {{ answers: Object, activeStep: string, completedSteps: string[] } | null}
 */
export function loadSignupAriDraft() {
  if (!canUseSessionStorage()) return null;
  try {
    const raw = window.sessionStorage.getItem(SIGNUP_ARI_DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      answers: {
        ...EMPTY_ANSWERS,
        ...(parsed.answers && typeof parsed.answers === 'object'
          ? parsed.answers
          : {}),
      },
      activeStep:
        typeof parsed.activeStep === 'string' &&
        SIGNUP_ARI_STEPS.includes(parsed.activeStep)
          ? parsed.activeStep
          : 'name',
      completedSteps: Array.isArray(parsed.completedSteps)
        ? parsed.completedSteps.filter((id) => SIGNUP_ARI_STEPS.includes(id))
        : [],
    };
  } catch (e) {
    return null;
  }
}

/**
 * @param {{ answers: Object, activeStep: string, completedSteps: string[] }} draft
 */
export function saveSignupAriDraft(draft) {
  if (!canUseSessionStorage() || !draft) return;
  try {
    window.sessionStorage.setItem(
      SIGNUP_ARI_DRAFT_KEY,
      JSON.stringify({
        answers: { ...EMPTY_ANSWERS, ...(draft.answers || {}) },
        activeStep: draft.activeStep || 'name',
        completedSteps: Array.isArray(draft.completedSteps)
          ? draft.completedSteps
          : [],
        savedAt: Date.now(),
      })
    );
  } catch (e) {
    // Ignore quota / private mode failures — chat still works in memory.
  }
}

export function clearSignupAriDraft() {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(SIGNUP_ARI_DRAFT_KEY);
  } catch (e) {
    // ignore
  }
}

/** Mark that `/ari` should auto-run a search once after signup-ari complete. */
export function markSignupAriPendingSearch() {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.setItem(SIGNUP_ARI_PENDING_SEARCH_KEY, '1');
  } catch (e) {
    // ignore
  }
}

export function hasSignupAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(SIGNUP_ARI_PENDING_SEARCH_KEY) === '1';
  } catch (e) {
    return false;
  }
}

/**
 * Read and clear the pending auto-search flag (one-shot).
 * @returns {boolean}
 */
export function consumeSignupAriPendingSearch() {
  if (!canUseSessionStorage()) return false;
  try {
    const pending =
      window.sessionStorage.getItem(SIGNUP_ARI_PENDING_SEARCH_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(SIGNUP_ARI_PENDING_SEARCH_KEY);
    }
    return pending;
  } catch (e) {
    return false;
  }
}

export function getEmptySignupAriAnswers() {
  return { ...EMPTY_ANSWERS, roles: [], experience: [], newsletter: null };
}

/**
 * Location string for Ari search criteria (city + country when both set).
 * @param {{ city?: string, country?: string }} answers
 */
export function buildSignupAriLocationLabel(answers = {}) {
  const city = String(answers.city || '').trim();
  const country = String(answers.country || '').trim();
  if (city && country) return `${city}, ${country}`;
  return city || country || '';
}

/**
 * Profile + criteria payload for post-auth persist.
 * @param {Object} answers
 */
export function buildSignupAriPersistPayload(answers = {}) {
  const name = String(answers.name || '').trim();
  const roles = Array.isArray(answers.roles)
    ? answers.roles
        .map((r) => String(r).trim())
        .filter(Boolean)
        .slice(0, 10)
    : [];
  const experience = Array.isArray(answers.experience)
    ? answers.experience.map((e) => String(e).trim()).filter(Boolean)
    : [];
  const country = String(answers.country || '').trim();
  const city = String(answers.city || '').trim();
  const countryCode = String(answers.countryCode || '').trim();
  const newsletter =
    typeof answers.newsletter === 'boolean' ? answers.newsletter : null;

  const location = country
    ? {
        city,
        country,
        country_code: countryCode || undefined,
        text: buildSignupAriLocationLabel({ city, country }),
        method: 'html5',
        countryOnly: city ? 'specificAddress' : 'countryOnly',
      }
    : null;

  const criteria = {
    interests: roles,
    excluded: [],
    location: buildSignupAriLocationLabel({ city, country }),
    // Default to all arrangement / contract options (same as Ari search defaults).
    arrangements: ['Remote', 'Hybrid', 'On-site'],
    experience,
    contractTypes: ['Contract', 'Freelance', 'Part-time', 'Temporary'],
    includeFullTime: false,
  };

  const emailPreferences = {};
  if (newsletter !== null) {
    // Same field as Settings / OnboardingNotifications weekly newsletter.
    emailPreferences.newsletter = newsletter;
  }
  if (experience.length > 0) {
    // Same field as Settings “Job experience levels” checkboxes.
    emailPreferences.job_alert_experience_level =
      buildJobAlertExperienceLevel(experience);
  }

  return {
    profile: {
      displayname: name,
      ...(location ? { location } : {}),
      ...(roles.length ? { featured_roles: roles } : {}),
      ...(Object.keys(emailPreferences).length
        ? {
            preferences: {
              email: emailPreferences,
            },
          }
        : {}),
    },
    criteria,
  };
}
